import React from 'react';
import {useMemoizedValue} from '@sail/react';
import type {Selection} from '@react-types/shared';
import {useController} from '../../lib/react-hook-form';
import {useFormContext} from './useFormContext';
import type {FieldValues, UseControllerProps} from './types';
import {getDummyControl} from './dummyControl';

export type FormFieldValue =
  | string
  | number
  | boolean
  | string[]
  | readonly string[]
  | null
  | File
  | undefined
  | Selection;

export type FormAdapter<TValue = FormFieldValue, TValueOutput = TValue> = {
  /**
   * Optional adapter function to transform the field value before passing it to the component.
   * This enables custom value logic (e.g., checkbox groups where fieldValue is an array
   * but component needs a boolean).
   */
  valueAdapter: (fieldValue: TValue, controlValue?: TValue) => TValueOutput;
  /**
   * Optional adapter function to transform the change event before updating the field.
   * This enables custom onChange logic (e.g., checkbox groups where individual checkbox
   * changes need to update an array).
   */
  changeAdapter: (
    eventOrValue: React.ChangeEvent<HTMLElement> | TValueOutput,
    fieldValue: TValue,
    controlValue?: TValue,
  ) => TValue;
};

type ComponentProps<TProps extends FormBaseProps> = Omit<TProps, 'rules'> & {
  disabled: boolean;
  error: string | undefined;
  onBlur?: () => void;
  name?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: any;
};

type FormBaseProps = UseControllerProps<FieldValues> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
  error?: React.ReactNode | void;
};

export type UseFormRegisterOptions<TComponentValue = FormFieldValue> = {
  valueProp?: string;
  changeProp?: string;
  valueAdapter?: FormAdapter<FormFieldValue, TComponentValue>['valueAdapter'];
  changeAdapter?: FormAdapter<FormFieldValue, TComponentValue>['changeAdapter'];
};

type UseFormRegisterProps<
  TProps extends FormBaseProps,
  TComponentValue = FormFieldValue,
> = {
  props: TProps;
} & UseFormRegisterOptions<TComponentValue>;

export const useFormRegister = <
  TProps extends FormBaseProps,
  TComponentValue = FormFieldValue,
>({
  props: originalProps,
  valueProp = 'value',
  changeProp = 'onChange',
  valueAdapter,
  changeAdapter,
}: UseFormRegisterProps<TProps, TComponentValue>): {
  isInSailForm: boolean;
  componentProps: ComponentProps<TProps> | TProps;
} => {
  const form = useFormContext();
  const props = useMemoizedValue(originalProps);

  const {field, fieldState, formState} = useController({
    control: form?.control || getDummyControl(),
    rules: props.rules,
    name: props.name || '__dummy__',
    defaultValue: props.defaultValue,
    disabled: props.disabled,
  });

  const adaptedValue = React.useMemo(() => {
    const base = valueAdapter
      ? valueAdapter(field.value, props.value)
      : field.value;

    // Coerce undefined -> null for the bound value prop so fields mount controlled
    // even when defaultValues arrive async. This prevents "controlled vs uncontrolled"
    // flips and ensures programmatic updates reflect correctly while editing.
    return base ?? null;
  }, [valueAdapter, field.value, props.value]);

  const adaptedOnChange = React.useCallback(
    (event: React.ChangeEvent<HTMLElement>) => {
      const transformedValue = changeAdapter
        ? changeAdapter(event, field.value, props.value)
        : event;

      field.onChange(transformedValue);

      const propsOnChangeCallback = props[changeProp as keyof TProps];
      if (typeof propsOnChangeCallback === 'function') {
        propsOnChangeCallback(event);
      }
    },
    [changeAdapter, changeProp, field, props],
  );

  const componentProps = React.useMemo(() => {
    const {value: _, onChange: __, ...restField} = field;

    // 1. Remove rules object, we don't want it in the component.
    // 2. Remove value, but only if valueProp is 'value'. Otherwise, we want
    //    both props (value and [valueProp]) to be available to the component.
    //    Certain components (e.g. Radio) need both props to be available.
    const restProps =
      valueProp === 'value'
        ? (({value, rules, ...rest}) => rest)(props)
        : (({rules, ...rest}) => rest)(props);

    const result = {
      ...restProps,
      ...restField,
      [valueProp]: adaptedValue,
      [changeProp]: adaptedOnChange,
      disabled: restProps.disabled || formState.disabled,
      error: fieldState.error?.message,
    } as ComponentProps<TProps>;
    return result;
  }, [
    props,
    field,
    formState.disabled,
    fieldState.error?.message,
    valueProp,
    changeProp,
    adaptedValue,
    adaptedOnChange,
  ]);

  // When not in a Sail Form context or name not provided, act as noop.
  // Return props as-is.
  if (!form?.control || !props.name) {
    return {
      isInSailForm: false,
      componentProps: props,
    };
  }

  return {
    isInSailForm: true,
    componentProps,
  };
};
